import { AuditPlatformShell, type AuditModule } from "./AuditPlatformShell";
import { AuditPortfolio } from "./AuditPortfolio";
import { PortfolioInsights } from "./PortfolioInsights";
import { readDrafts } from "./storage";
import { QueryManagement } from "./QueryManagement";
import { QueryAssistance } from "./QueryAssistance";
import { AuditCollaboration } from "./AuditCollaboration";
import { useState, type ReactNode, type FormEvent } from "react";
import { getSession } from "../api";
import type { Client } from "../clientdesk/types";
import {
  PACK_VERSION,
  SOURCES,
  STAGES,
  CARO_TOPICS,
  createFile,
  presentation,
  amounts,
  materialityErrors,
  importTb,
  tbSummary,
  syncConditionalProcedures,
  prepareProcedure,
  reviewProcedure,
  editProcedure,
  blockers,
  advance,
  record,
  newId,
  exportCsv,
  validDate,
  type AuditFile,
  type Procedure,
  type Risk,
  type Finding,
  type Request,
  type Framework,
} from "./india";
import "./india.css";
import "./platform.css";
const money = (n: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(n);
function download(name: string, content: string, type = "text/plain") {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="afi-field">
      <span>{label}</span>
      {children}
    </label>
  );
}
function Panel({
  title,
  children,
  aside,
}: {
  title: string;
  children: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <section className="afi-panel">
      <header>
        <h3>{title}</h3>
        {aside}
      </header>
      {children}
    </section>
  );
}
function Tag({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: string;
}) {
  return <span className={`afi-tag ${tone}`}>{children}</span>;
}
const tabs = [
  "Overview",
  "Acceptance",
  "Trial balance",
  "Risks",
  "Working papers",
  "CARO",
  "Client requests",
  "Queries",
  "Query assistance",
  "Findings",
  "Completion",
  "Methodology",
] as const;
type Tab = (typeof tabs)[number];
export function IndiaAuditWorkspace({
  clients,
  navigate,
  onLegacy,
}: {
  clients: Client[];
  navigate: (v: string) => void;
  onLegacy: () => void;
}) {
  const session = getSession();
  const scope = `ledzerly-auditflow-india-v1:${session?.user.firmId ?? "evaluation"}:${session?.user.sub ?? "local"}`;
  const [loaded] = useState(() => readDrafts(scope));
  const [module, setModule] = useState<AuditModule>("dashboard");
  const [files, setFiles] = useState(loaded.files);
  const [error, setError] = useState(loaded.error);
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [tab, setTab] = useState<Tab>("Overview");
  const [actor, setActor] = useState("Evaluation preparer");
  const file = files.find((f) => f.id === selected);
  function persist(next: AuditFile[]) {
    if (loaded.error) throw new Error(loaded.error);
    try {
      localStorage.setItem(scope, JSON.stringify(next));
      setFiles(next);
      setError("");
    } catch (e) {
      throw new Error(
        e instanceof Error
          ? e.message
          : "Browser storage is full; export the current engagement.",
      );
    }
  }
  const attempt = (fn: () => void) => {
    try {
      fn();
      setError("");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  const save = (updated: AuditFile, action: string) => {
    if (file?.stage === "Archived")
      throw new Error("Archived evaluation files are read-only.");
    persist(
      files.map((f) =>
        f.id === updated.id ? record(updated, actor, action) : f,
      ),
    );
  };
  const progress = (f: AuditFile) =>
    Math.round(
      (f.procedures.filter((p) => p.status === "reviewed").length /
        Math.max(f.procedures.length, 1)) *
        100,
    );
  const openModule = (next: AuditModule) => {
    setModule(next);
    setSelected(null);
    setCreating(false);
  };
  const openFile = (id: string, section: Tab) => {
    setSelected(id);
    setModule("workpapers");
    setCreating(false);
    setTab(section);
  };
  return (
    <AuditPlatformShell
      module={module}
      fileName={file?.clientName}
      userName={session?.user.displayName ?? "Audit team"}
      onModule={openModule}
      onSuite={() => navigate("suite")}
      onLegacy={onLegacy}
    >
      {module === "collaboration" ? (
        <AuditCollaboration onBack={() => openModule("dashboard")} />
      ) : (
        <>
          <div className="afi-notice">
            <strong>
              <span className="afb-draft-dot" /> Browser drafts
            </strong>
            <span>
              Audit workpapers save in this browser. Review identities are
              simulated. Shared queries and evidence save separately in
              Collaborate.
            </span>
            <button
              className="afi-link afb-shared-link"
              onClick={() => openModule("collaboration")}
            >
              Shared queries & client portal ↗
            </button>
            <Field label="Evaluation actor">
              <select value={actor} onChange={(e) => setActor(e.target.value)}>
                <option>Evaluation preparer</option>
                <option>Evaluation reviewer</option>
                <option>Evaluation partner</option>
              </select>
            </Field>
          </div>
          {error && (
            <div role="alert" className="afi-error">
              {error}
            </div>
          )}
          {module === "analytics" || module === "quality" ? (
            <PortfolioInsights
              key={module}
              files={files}
              mode={module}
              onOpen={openFile}
            />
          ) : module === "methodology" ? (
            <Methodology />
          ) : creating ? (
            <NewFile
              clients={clients}
              onCancel={() => setCreating(false)}
              onCreate={(input) =>
                attempt(() => {
                  const f = createFile(input, actor);
                  persist([f, ...files]);
                  setSelected(f.id);
                  setModule("workpapers");
                  setCreating(false);
                  setTab("Acceptance");
                })
              }
            />
          ) : !file ? (
            <AuditPortfolio
              files={files}
              register={module === "workpapers"}
              onCreate={() => setCreating(true)}
              onOpen={openFile}
              onModule={openModule}
            />
          ) : (
            <>
              <div className="afi-title">
                <div>
                  <button
                    className="afi-link"
                    onClick={() => setSelected(null)}
                  >
                    ← All engagements
                  </button>
                  <h1>{file.clientName}</h1>
                  <p>
                    {file.framework} ·{" "}
                    {file.consolidated ? "Consolidated" : "Standalone"} · Year
                    ended {file.periodEnd} · {presentation(file)}
                  </p>
                </div>
                <button
                  onClick={() =>
                    download(
                      `${file.clientName.replace(/[^a-z0-9]/gi, "-")}-audit-draft.json`,
                      JSON.stringify(file, null, 2),
                      "application/json",
                    )
                  }
                >
                  Export draft file
                </button>
              </div>
              <ol className="afi-stages">
                {STAGES.map((s, i) => (
                  <li
                    key={s}
                    className={
                      s === file.stage
                        ? "active"
                        : STAGES.indexOf(file.stage) > i
                          ? "done"
                          : ""
                    }
                  >
                    <span>{String(i + 1).padStart(2, "0")}</span>
                    {s}
                  </li>
                ))}
              </ol>
              <div className="afi-layout">
                <nav aria-label="Engagement sections">
                  {tabs.map((t) => (
                    <button
                      key={t}
                      className={tab === t ? "active" : ""}
                      onClick={() => setTab(t)}
                    >
                      {t}
                      {t === "Working papers" && (
                        <span>{file.procedures.length}</span>
                      )}
                      {t === "Findings" && (
                        <span>
                          {
                            file.findings.filter(
                              (f) => f.disposition === "open",
                            ).length
                          }
                        </span>
                      )}
                    </button>
                  ))}
                  <div className="afi-nav-note">
                    {progress(file)}% reviewed
                    <progress value={progress(file)} max={100} />
                    <small>
                      {
                        file.procedures.filter((p) => p.status === "reviewed")
                          .length
                      }{" "}
                      / {file.procedures.length} procedures
                    </small>
                  </div>
                </nav>
                <main key={`${file.id}-${tab}`}>
                  {tab === "Overview" ? (
                    <>
                      <div className="afi-grid">
                        <Panel title="Next stage">
                          <h2>
                            {STAGES[STAGES.indexOf(file.stage) + 1] ??
                              "File archived"}
                          </h2>
                          {file.stage === "Archived" ? (
                            <p>This evaluation snapshot is read-only.</p>
                          ) : (
                            <>
                              <ul className="afi-blockers">
                                {blockers(
                                  file,
                                  STAGES[STAGES.indexOf(file.stage) + 1],
                                ).map((b) => (
                                  <li key={b}>{b}</li>
                                ))}
                              </ul>
                              {!blockers(
                                file,
                                STAGES[STAGES.indexOf(file.stage) + 1],
                              ).length && (
                                <p>
                                  Workflow checks for the next stage are
                                  complete.
                                </p>
                              )}
                              <button
                                className="afi-primary"
                                onClick={() =>
                                  attempt(() => {
                                    const updated = advance(file, actor);
                                    persist(
                                      files.map((f) =>
                                        f.id === file.id ? updated : f,
                                      ),
                                    );
                                  })
                                }
                                disabled={
                                  blockers(
                                    file,
                                    STAGES[STAGES.indexOf(file.stage) + 1],
                                  ).length > 0
                                }
                              >
                                Move to {STAGES[STAGES.indexOf(file.stage) + 1]}
                              </button>
                            </>
                          )}
                        </Panel>
                        <Panel title="File at a glance">
                          <dl>
                            <dt>Engagement partner</dt>
                            <dd>{file.partner}</dd>
                            <dt>Methodology version</dt>
                            <dd>{file.version}</dd>
                            <dt>Open evidence requests</dt>
                            <dd>
                              {
                                file.requests.filter(
                                  (r) => r.status !== "accepted",
                                ).length
                              }
                            </dd>
                            <dt>Unadjusted findings — absolute amounts</dt>
                            <dd>
                              {money(
                                file.findings
                                  .filter((f) => f.disposition === "unadjusted")
                                  .reduce((n, f) => n + Math.abs(f.amount), 0),
                              )}
                            </dd>
                          </dl>
                        </Panel>
                      </div>
                      <Panel title="Activity log">
                        <p className="afi-muted">
                          Browser activity history is for evaluation; it is not
                          an immutable audit record.
                        </p>
                        <div className="afi-events">
                          {file.events
                            .slice(-12)
                            .reverse()
                            .map((e, i) => (
                              <div key={i}>
                                <time>
                                  {new Date(e.at).toLocaleString("en-IN")}
                                </time>
                                <strong>{e.action}</strong>
                                <span>{e.actor}</span>
                              </div>
                            ))}
                        </div>
                      </Panel>
                    </>
                  ) : (
                    <fieldset
                      disabled={file.stage === "Archived"}
                      className="afi-edit-area"
                    >
                      {tab === "Acceptance" && (
                        <Acceptance
                          file={file}
                          onSave={(f) =>
                            attempt(() =>
                              save(
                                syncConditionalProcedures(f),
                                "Updated acceptance and applicability",
                              ),
                            )
                          }
                        />
                      )}
                      {tab === "Trial balance" && (
                        <TrialBalance
                          file={file}
                          onSave={(f, a) => attempt(() => save(f, a))}
                          onError={setError}
                        />
                      )}
                      {tab === "Risks" && (
                        <Risks
                          file={file}
                          onSave={(f, a) => attempt(() => save(f, a))}
                        />
                      )}
                      {(tab === "Working papers" || tab === "CARO") && (
                        <Workpapers
                          file={file}
                          actor={actor}
                          caro={tab === "CARO"}
                          onSave={(f, a) => attempt(() => save(f, a))}
                          onError={setError}
                        />
                      )}
                      {tab === "Client requests" && (
                        <Requests
                          file={file}
                          onSave={(f, a) => attempt(() => save(f, a))}
                          onError={setError}
                        />
                      )}
                      {tab === "Findings" && (
                        <Findings
                          file={file}
                          onSave={(f, a) => attempt(() => save(f, a))}
                        />
                      )}
                      {tab === "Queries" && (
                        <QueryManagement
                          key={file.id}
                          file={file}
                          actor={actor}
                          onSave={(f, a) => save(f, a)}
                        />
                      )}
                      {tab === "Query assistance" && (
                        <QueryAssistance
                          key={file.id}
                          file={file}
                          files={files}
                          actor={actor}
                          onSave={save}
                        />
                      )}
                      {tab === "Completion" && (
                        <Completion
                          file={file}
                          onSave={(f, a) => attempt(() => save(f, a))}
                        />
                      )}
                      {tab === "Methodology" && <Methodology />}
                    </fieldset>
                  )}
                </main>
              </div>
            </>
          )}
          <footer className="afb-footer">
            Ledzerly AuditFlow · India methodology {PACK_VERSION}{" "}
            <span>Working papers · Analytics · Collaboration · Quality</span>
          </footer>
        </>
      )}
    </AuditPlatformShell>
  );
}
function NewFile({
  clients,
  onCreate,
  onCancel,
}: {
  clients: Client[];
  onCreate: (
    input: Pick<
      AuditFile,
      | "clientId"
      | "clientName"
      | "periodEnd"
      | "framework"
      | "entity"
      | "partner"
      | "consolidated"
    >,
  ) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [clientId, setClientId] = useState("");
  const [period, setPeriod] = useState("");
  const [framework, setFramework] = useState<Framework>("AS");
  const [entity, setEntity] = useState<AuditFile["entity"]>("company");
  const [partner, setPartner] = useState("");
  const [consolidated, setConsolidated] = useState(false);
  return (
    <form
      className="afi-new"
      onSubmit={(e) => {
        e.preventDefault();
        onCreate({
          clientId,
          clientName: name,
          periodEnd: period,
          framework,
          entity,
          partner,
          consolidated,
        });
      }}
    >
      <Panel title="New Indian financial-statement audit">
        <p>
          Create a draft file for a financial-statement audit. Tax audits,
          reviews and other engagements retain their separate workflows.
        </p>
        <div className="afi-grid">
          <Field label="Existing ClientDesk client (optional)">
            <select
              value={clientId}
              onChange={(e) => {
                setClientId(e.target.value);
                setName(
                  clients.find((c) => c.id === e.target.value)?.name ?? "",
                );
              }}
            >
              <option value="">Enter client manually</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Client / legal name">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </Field>
          <Field label="Period end">
            <input
              type="date"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              required
            />
          </Field>
          <Field label="Engagement partner">
            <input
              value={partner}
              onChange={(e) => setPartner(e.target.value)}
              required
            />
          </Field>
          <Field label="Financial reporting framework">
            <select
              value={framework}
              onChange={(e) => setFramework(e.target.value as Framework)}
            >
              <option>AS</option>
              <option>Ind AS</option>
            </select>
          </Field>
          <Field label="Entity presentation">
            <select
              value={entity}
              onChange={(e) => setEntity(e.target.value as AuditFile["entity"])}
            >
              <option value="company">Company — general purpose</option>
              <option value="nbfc">NBFC — Ind AS foundation</option>
              <option value="llp">LLP</option>
              <option value="other">Other non-corporate entity</option>
            </select>
          </Field>
        </div>
        <label className="afi-check">
          <input
            type="checkbox"
            checked={consolidated}
            onChange={(e) => setConsolidated(e.target.checked)}
          />
          Consolidated financial statements
        </label>
        <p className="afi-muted">
          Sector-specific reporting and regulatory supplements must be added for
          regulated entities. This pack is a starting programme, not an
          exhaustive compliance checklist.
        </p>
        <div className="afi-actions">
          <button type="button" onClick={onCancel}>
            Cancel
          </button>
          <button className="afi-primary">Create engagement</button>
        </div>
      </Panel>
    </form>
  );
}
type Save = (f: AuditFile, action: string) => void;
function Acceptance({
  file,
  onSave,
}: {
  file: AuditFile;
  onSave: (f: AuditFile) => void;
}) {
  const [f, setF] = useState(file);
  const locked = STAGES.indexOf(file.stage) > 1;
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave(f);
      }}
    >
      <Panel title="Acceptance and reporting scope">
        <p>
          Record the facts and your judgement. A company is not automatically
          subject to every reporting requirement.
        </p>
        {locked && (
          <p className="afi-muted">
            Applicability is frozen after planning in this evaluation build.
          </p>
        )}
        <fieldset disabled={locked} className="afi-edit-area">
          <Field label="Framework and presentation rationale">
            <textarea
              value={f.frameworkRationale}
              onChange={(e) =>
                setF({ ...f, frameworkRationale: e.target.value })
              }
              placeholder="Entity facts, applicable rules, exemptions and financial year considered"
              required
            />
          </Field>
          <div className="afi-grid">
            <Field label="Signed engagement letter — evidence reference">
              <input
                value={f.acceptance.letterRef}
                onChange={(e) =>
                  setF({
                    ...f,
                    acceptance: { ...f.acceptance, letterRef: e.target.value },
                  })
                }
                required
              />
            </Field>
            <Field label="Predecessor communication / reason not applicable">
              <input
                value={f.acceptance.predecessor}
                onChange={(e) =>
                  setF({
                    ...f,
                    acceptance: {
                      ...f.acceptance,
                      predecessor: e.target.value,
                    },
                  })
                }
                required
              />
            </Field>
          </div>
          {(
            [
              ["independence", "Team independence and safeguards evaluated"],
              ["ethics", "Ethics and client acceptance checks documented"],
              ["appointment", "Appointment, eligibility and scope verified"],
            ] as const
          ).map(([k, label]) => (
            <label className="afi-check" key={k}>
              <input
                type="checkbox"
                checked={f.acceptance[k]}
                onChange={(e) =>
                  setF({
                    ...f,
                    acceptance: { ...f.acceptance, [k]: e.target.checked },
                  })
                }
              />
              {label}
            </label>
          ))}
          <div className="afi-applicability">
            {(
              [
                ["caro", "CARO 2020"],
                ["ifc", "Internal financial controls — 143(3)(i)"],
                ["kam", "Key audit matters — SA 701"],
                ["auditTrail", "Software audit-trail reporting — Rule 11(g)"],
              ] as const
            ).map(([k, label]) => (
              <div key={k}>
                <strong>{label}</strong>
                <Field label={`${label}: applicability`}>
                  <select
                    value={f.applicability[k].decision}
                    onChange={(e) =>
                      setF({
                        ...f,
                        applicability: {
                          ...f.applicability,
                          [k]: {
                            ...f.applicability[k],
                            decision: e.target.value,
                          },
                        },
                      })
                    }
                  >
                    <option value="unassessed">Needs assessment</option>
                    <option value="applicable">Applicable</option>
                    <option value="not-applicable">Not applicable</option>
                  </select>
                </Field>
                <Field label={`${label}: rationale`}>
                  <textarea
                    value={f.applicability[k].rationale}
                    onChange={(e) =>
                      setF({
                        ...f,
                        applicability: {
                          ...f.applicability,
                          [k]: {
                            ...f.applicability[k],
                            rationale: e.target.value,
                          },
                        },
                      })
                    }
                    placeholder="Record conditions, current thresholds / exemption, source and conclusion"
                    required
                  />
                </Field>
              </div>
            ))}
          </div>
          <button className="afi-primary">
            Save acceptance & generate applicable procedures
          </button>
        </fieldset>
      </Panel>
    </form>
  );
}
function TrialBalance({
  file,
  onSave,
  onError,
}: {
  file: AuditFile;
  onSave: Save;
  onError: (e: string) => void;
}) {
  const [text, setText] = useState("");
  const [preview, setPreview] = useState<AuditFile["tb"] | null>(null);
  const [source, setSource] = useState(file.tbSource);
  const [m, setM] = useState(file.materiality);
  const [reconciled, setReconciled] = useState(file.tbReconciled);
  const summary = tbSummary(preview ?? file.tb);
  const materiality = amounts(m);
  const locked = STAGES.indexOf(file.stage) > 1;
  return (
    <>
      <Panel
        title="Trial balance import"
        aside={
          <button
            type="button"
            onClick={() =>
              download(
                "auditflow-trial-balance-template.csv",
                "code,name,current,prior,mapping\n1000,Bank,100000,80000,Cash and cash equivalents\n3000,Equity,-100000,-80000,Equity\n",
                "text/csv",
              )
            }
          >
            Download CSV template
          </button>
        }
      >
        <p>
          Signed amounts: debit positive, credit negative. Both years must
          balance. Map each account to a lead schedule. The template contains
          illustrative rows.
        </p>
        <fieldset disabled={locked} className="afi-edit-area">
          {locked && (
            <p>
              Data imports are frozen after planning in this evaluation build.
            </p>
          )}
          <Field label="Choose a CSV file">
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={async (e) => {
                const upload = e.target.files?.[0];
                if (!upload) return;
                try {
                  if (upload.size > 5_000_000)
                    throw new Error("CSV limit is 5 MB.");
                  const body = await upload.text();
                  setText(body);
                  setPreview(importTb(body));
                  onError("");
                } catch (error) {
                  setPreview(null);
                  onError((error as Error).message);
                }
              }}
            />
          </Field>
          <Field label="Or paste trial-balance CSV">
            <textarea
              rows={4}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                setPreview(null);
              }}
              placeholder="code,name,current,prior,mapping"
            />
          </Field>
          <button
            type="button"
            onClick={() => {
              try {
                setPreview(importTb(text));
                onError("");
              } catch (e) {
                setPreview(null);
                onError((e as Error).message);
              }
            }}
          >
            Validate & preview
          </button>
          <div className="afi-stats small">
            <div>
              <span>Current-year difference</span>
              <strong>{money(summary.currentDifference)}</strong>
            </div>
            <div>
              <span>Prior-year difference</span>
              <strong>{money(summary.priorDifference)}</strong>
            </div>
            <div>
              <span>Unmapped accounts</span>
              <strong>{summary.unmapped}</strong>
            </div>
          </div>
          <Field label="Source and reconciliation reference">
            <input
              value={source}
              onChange={(e) => setSource(e.target.value)}
              placeholder="Export name, ledger totals and reconciliation workpaper"
            />
          </Field>
          <label className="afi-check">
            <input
              type="checkbox"
              checked={reconciled}
              onChange={(e) => setReconciled(e.target.checked)}
            />
            I reconciled the population to the underlying books and prior-year
            records.
          </label>
          <button
            className="afi-primary"
            type="button"
            disabled={
              !(preview ?? file.tb).length ||
              !source.trim() ||
              !reconciled ||
              summary.currentDifference !== 0 ||
              summary.priorDifference !== 0 ||
              summary.unmapped > 0
            }
            onClick={() => {
              onSave(
                {
                  ...file,
                  tb: preview ?? file.tb,
                  tbSource: source,
                  tbReconciled: reconciled,
                },
                "Saved validated trial balance",
              );
              setPreview(null);
            }}
          >
            Save trial balance
          </button>
        </fieldset>
      </Panel>
      {(preview ?? file.tb).length > 0 && (
        <Panel title={preview ? "Import preview" : "Saved lead schedules"}>
          <div className="afi-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Lead schedule</th>
                  <th>Current</th>
                  <th>Prior</th>
                  <th>Movement</th>
                  <th>Review indicator</th>
                </tr>
              </thead>
              <tbody>
                {(preview ?? file.tb).map((r) => (
                  <tr key={r.code}>
                    <td>
                      {r.code}
                      <small>{r.name}</small>
                    </td>
                    <td>{r.mapping || "Unmapped"}</td>
                    <td>{money(r.current)}</td>
                    <td>{money(r.prior)}</td>
                    <td>{money(r.current - r.prior)}</td>
                    <td>
                      {r.prior === 0 && r.current !== 0 ? (
                        <Tag tone="amber">New balance</Tag>
                      ) : r.prior !== 0 &&
                        Math.abs((r.current - r.prior) / r.prior) >= 0.25 ? (
                        <Tag tone="amber">Movement ≥25%</Tag>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="afi-muted">
            Variance indicators prompt investigation; they do not establish a
            misstatement. The 25% threshold is an evaluation rule.
          </p>
        </Panel>
      )}
      <Panel title="Materiality — SA 320">
        <p>
          Enter engagement-specific judgements. These percentages are not
          mandated by the standard.
        </p>
        <fieldset disabled={locked} className="afi-edit-area">
          <div className="afi-grid">
            <Field label="Benchmark">
              <select
                value={m.benchmark}
                onChange={(e) => setM({ ...m, benchmark: e.target.value })}
              >
                {[
                  "Profit before tax",
                  "Revenue",
                  "Total assets",
                  "Equity",
                  "Expenditure",
                  "Other normalised benchmark",
                ].map((b) => (
                  <option key={b}>{b}</option>
                ))}
              </select>
            </Field>
            {(
              [
                ["base", "Benchmark amount (₹)"],
                ["percent", "Overall materiality (%)"],
                ["performancePercent", "Performance (% of overall)"],
                ["trivialPercent", "Clearly trivial (% of overall)"],
              ] as const
            ).map(([k, label]) => (
              <Field key={k} label={label}>
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={m[k]}
                  onChange={(e) => setM({ ...m, [k]: Number(e.target.value) })}
                />
              </Field>
            ))}
          </div>
          <Field label="Rationale and qualitative considerations">
            <textarea
              value={m.rationale}
              onChange={(e) => setM({ ...m, rationale: e.target.value })}
            />
          </Field>
          <div className="afi-callout">
            Overall {money(materiality.overall)} · Performance{" "}
            {money(materiality.performance)} · Clearly trivial{" "}
            {money(materiality.trivial)}
          </div>
          <button
            className="afi-primary"
            type="button"
            onClick={() => {
              const errors = materialityErrors(m);
              if (errors.length) {
                onError(errors.join(" "));
                return;
              }
              onSave(
                { ...file, materiality: m },
                "Saved materiality judgement",
              );
            }}
          >
            Save materiality
          </button>
        </fieldset>
      </Panel>
    </>
  );
}
function Risks({ file, onSave }: { file: AuditFile; onSave: Save }) {
  const [title, setTitle] = useState("");
  const [assertion, setAssertion] = useState("Occurrence");
  const [level, setLevel] = useState<Risk["level"]>("High");
  const [response, setResponse] = useState("");
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !response.trim()) return;
    onSave(
      {
        ...file,
        risks: [
          ...file.risks,
          { id: newId(), title, assertion, level, response },
        ],
      },
      "Added assessed risk",
    );
    setTitle("");
    setResponse("");
  };
  return (
    <>
      <Panel title="Risk assessment">
        <p>
          Connect each risk to an assertion and a response, then link it to the
          relevant working-paper procedures.
        </p>
        <form onSubmit={submit}>
          <div className="afi-grid">
            <Field label="Risk description">
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </Field>
            <Field label="Assertion">
              <select
                value={assertion}
                onChange={(e) => setAssertion(e.target.value)}
              >
                {[
                  "Occurrence",
                  "Existence",
                  "Completeness",
                  "Accuracy",
                  "Valuation",
                  "Cut-off",
                  "Rights and obligations",
                  "Classification",
                  "Presentation",
                ].map((a) => (
                  <option key={a}>{a}</option>
                ))}
              </select>
            </Field>
            <Field label="Assessed risk">
              <select
                value={level}
                onChange={(e) => setLevel(e.target.value as Risk["level"])}
              >
                {["Low", "Moderate", "High", "Significant"].map((l) => (
                  <option key={l}>{l}</option>
                ))}
              </select>
            </Field>
            <Field label="Planned response">
              <textarea
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                required
              />
            </Field>
          </div>
          <button className="afi-primary">Add risk</button>
        </form>
      </Panel>
      <Panel title="Risk-to-procedure coverage">
        {!file.risks.length ? (
          <p>No risks recorded yet.</p>
        ) : (
          file.risks.map((r) => {
            const linked = file.procedures.filter((p) =>
              p.riskIds.includes(r.id),
            );
            return (
              <article className="afi-risk" key={r.id}>
                <div>
                  <Tag
                    tone={
                      r.level === "High" || r.level === "Significant"
                        ? "amber"
                        : "neutral"
                    }
                  >
                    {r.level}
                  </Tag>
                  <strong>{r.title}</strong>
                  <small>{r.assertion}</small>
                </div>
                <p>{r.response}</p>
                <span>
                  {linked.length
                    ? `Linked: ${linked.map((p) => p.id).join(", ")}`
                    : "No linked procedure — blocks fieldwork"}
                </span>
              </article>
            );
          })
        )}
      </Panel>
    </>
  );
}
function Workpapers({
  file,
  actor,
  caro,
  onSave,
  onError,
}: {
  file: AuditFile;
  actor: string;
  caro: boolean;
  onSave: Save;
  onError: (e: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("All");
  const [selected, setSelected] = useState<string | null>(null);
  const proc = file.procedures.find((p) => p.id === selected);
  const filtered = file.procedures.filter(
    (p) =>
      (caro ? p.area === "CARO" : p.area !== "CARO") &&
      (filter === "All" || p.status === filter) &&
      (p.title + " " + p.id + " " + p.standard)
        .toLowerCase()
        .includes(query.toLowerCase()),
  );
  const update = (p: Procedure, action: string) =>
    onSave(
      {
        ...file,
        procedures: file.procedures.map((x) => (x.id === p.id ? p : x)),
      },
      action,
    );
  return (
    <>
      <Panel
        title={caro ? "CARO 2020 workpapers" : "Working papers"}
        aside={<Tag>{filtered.length} procedures</Tag>}
      >
        {caro && (
          <p>
            Applicability: {file.applicability.caro.decision}.{" "}
            {file.consolidated
              ? "Consolidated file: clause 3(xxi) programme."
              : "Standalone file: clauses 3(i)–3(xx) programme."}{" "}
            Each topic requires assessment of its underlying subclauses; this is
            not a pre-approved report.
          </p>
        )}
        <div className="afi-toolbar">
          <input
            aria-label="Search procedures"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search area, reference or standard…"
          />
          <select
            aria-label="Filter procedure status"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option>All</option>
            <option value="open">Open</option>
            <option value="prepared">Prepared</option>
            <option value="reviewed">Reviewed</option>
          </select>
        </div>
        {!filtered.length ? (
          <p>
            No matching procedures. Review applicability in Acceptance to
            generate conditional programmes.
          </p>
        ) : (
          <div className="afi-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Ref.</th>
                  <th>Procedure / standard</th>
                  <th>Status</th>
                  <th>Evidence</th>
                  <th>Risk links</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id}>
                    <td>{p.id}</td>
                    <td>
                      <button
                        className="afi-link"
                        onClick={() => setSelected(p.id)}
                      >
                        {p.title}
                      </button>
                      <small>{p.standard}</small>
                    </td>
                    <td>
                      <Tag
                        tone={
                          p.status === "reviewed"
                            ? "green"
                            : p.status === "prepared"
                              ? "amber"
                              : "neutral"
                        }
                      >
                        {p.status}
                      </Tag>
                    </td>
                    <td>
                      {p.naReason
                        ? "N/A documented"
                        : p.evidence
                          ? "Referenced"
                          : "Missing"}
                    </td>
                    <td>{p.riskIds.length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      {proc && (
        <ProcedureEditor
          key={`${proc.id}-${proc.status}-${proc.preparedAt ?? ""}`}
          proc={proc}
          risks={file.risks}
          actor={actor}
          onClose={() => setSelected(null)}
          onSave={update}
          onError={onError}
        />
      )}
    </>
  );
}
function ProcedureEditor({
  proc,
  risks,
  actor,
  onClose,
  onSave,
  onError,
}: {
  proc: Procedure;
  risks: Risk[];
  actor: string;
  onClose: () => void;
  onSave: (p: Procedure, a: string) => void;
  onError: (e: string) => void;
}) {
  const [draft, setDraft] = useState(proc);
  const changed = JSON.stringify(draft) !== JSON.stringify(proc);
  const act = (fn: () => Procedure, label: string) => {
    try {
      onSave(fn(), label);
    } catch (e) {
      onError((e as Error).message);
    }
  };
  const current = () =>
    changed
      ? editProcedure(proc, {
          evidence: draft.evidence,
          conclusion: draft.conclusion,
          naReason: draft.naReason,
          riskIds: draft.riskIds,
          reviewNote: draft.reviewNote,
        })
      : proc;
  return (
    <Panel
      title={`${proc.id} · ${proc.title}`}
      aside={
        <button type="button" onClick={onClose}>
          Close editor
        </button>
      }
    >
      <p>{proc.objective}</p>
      <Tag>{proc.standard}</Tag>
      <div className="afi-grid">
        <Field label="Evidence references">
          <textarea
            value={draft.evidence}
            onChange={(e) => setDraft({ ...draft, evidence: e.target.value })}
            placeholder="Document IDs, ledger references, sample selections and results"
          />
        </Field>
        <Field label="Work performed and conclusion">
          <textarea
            value={draft.conclusion}
            onChange={(e) => setDraft({ ...draft, conclusion: e.target.value })}
            rows={4}
          />
        </Field>
        <Field label="Not applicable — reason, if relevant">
          <textarea
            value={draft.naReason}
            onChange={(e) => setDraft({ ...draft, naReason: e.target.value })}
          />
        </Field>
        <Field label="Review notes">
          <textarea
            value={draft.reviewNote ?? ""}
            onChange={(e) => setDraft({ ...draft, reviewNote: e.target.value })}
          />
        </Field>
      </div>
      <div className="afi-risk-links">
        <strong>Responds to risks</strong>
        {risks.length ? (
          risks.map((r) => (
            <label className="afi-check" key={r.id}>
              <input
                type="checkbox"
                checked={draft.riskIds.includes(r.id)}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    riskIds: e.target.checked
                      ? [...draft.riskIds, r.id]
                      : draft.riskIds.filter((id) => id !== r.id),
                  })
                }
              />
              {r.title}
            </label>
          ))
        ) : (
          <p>Record risks in the Risks section first.</p>
        )}
      </div>
      <p className="afi-muted">
        Editing evidence, conclusions or risk links reopens preparation and
        review. Evidence references do not upload or verify files.
      </p>
      <div className="afi-actions">
        <button onClick={() => act(current, `Saved ${proc.id} draft`)}>
          Save draft
        </button>
        <button
          className="afi-primary"
          onClick={() =>
            act(() => prepareProcedure(current(), actor), `Prepared ${proc.id}`)
          }
        >
          Mark prepared
        </button>
        <button
          disabled={
            changed || proc.status !== "prepared" || actor === proc.preparedBy
          }
          onClick={() =>
            act(
              () => reviewProcedure(proc, actor),
              `Independently reviewed ${proc.id}`,
            )
          }
        >
          Record independent review
        </button>
      </div>
      <p className="afi-muted">
        Prepared: {proc.preparedBy ?? "Pending"} · Reviewed:{" "}
        {proc.reviewedBy ?? "Pending"}
      </p>
    </Panel>
  );
}
function Requests({
  file,
  onSave,
  onError,
}: {
  file: AuditFile;
  onSave: Save;
  onError: (e: string) => void;
}) {
  const [title, setTitle] = useState("");
  const [owner, setOwner] = useState("");
  const [due, setDue] = useState("");
  return (
    <>
      <Panel title="Client evidence requests">
        <p>
          Track what is needed, who owns it and whether the response is
          sufficient. Requests are recorded here; no messages are sent.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!validDate(due)) {
              onError("Enter a valid due date.");
              return;
            }
            onSave(
              {
                ...file,
                requests: [
                  ...file.requests,
                  {
                    id: newId(),
                    title,
                    owner,
                    due,
                    status: "requested",
                    evidence: "",
                    note: "",
                  },
                ],
              },
              "Added client evidence request",
            );
            setTitle("");
          }}
        >
          <div className="afi-grid">
            <Field label="Document / information requested">
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </Field>
            <Field label="Client owner">
              <input
                value={owner}
                onChange={(e) => setOwner(e.target.value)}
                required
              />
            </Field>
            <Field label="Due date">
              <input
                type="date"
                value={due}
                onChange={(e) => setDue(e.target.value)}
                required
              />
            </Field>
          </div>
          <button className="afi-primary">Add request</button>
        </form>
      </Panel>
      {file.requests.map((r) => (
        <RequestRow
          key={r.id}
          request={r}
          onSave={(updated) =>
            onSave(
              {
                ...file,
                requests: file.requests.map((x) =>
                  x.id === r.id ? updated : x,
                ),
              },
              `Updated evidence request: ${r.title}`,
            )
          }
          onError={onError}
        />
      ))}
    </>
  );
}
function RequestRow({
  request: r,
  onSave,
  onError,
}: {
  request: Request;
  onSave: (r: Request) => void;
  onError: (e: string) => void;
}) {
  const [draft, setDraft] = useState(r);
  return (
    <Panel
      title={r.title}
      aside={
        <Tag tone={r.status === "accepted" ? "green" : "amber"}>{r.status}</Tag>
      }
    >
      <p>
        {r.owner} · Due {r.due}
      </p>
      <div className="afi-grid">
        <Field label="Response evidence reference">
          <input
            value={draft.evidence}
            onChange={(e) => setDraft({ ...draft, evidence: e.target.value })}
          />
        </Field>
        <Field label="Status">
          <select
            value={draft.status}
            onChange={(e) =>
              setDraft({
                ...draft,
                status: e.target.value as Request["status"],
              })
            }
          >
            {["requested", "received", "accepted", "rejected"].map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Review / rejection note">
        <textarea
          value={draft.note}
          onChange={(e) => setDraft({ ...draft, note: e.target.value })}
        />
      </Field>
      <button
        onClick={() => {
          if (
            (draft.status === "accepted" || draft.status === "received") &&
            !draft.evidence.trim()
          ) {
            onError("Received / accepted evidence needs a reference.");
            return;
          }
          if (
            (draft.status === "accepted" || draft.status === "rejected") &&
            !draft.note.trim()
          ) {
            onError("Document your acceptance or rejection rationale.");
            return;
          }
          onSave(draft);
        }}
      >
        Save response
      </button>
    </Panel>
  );
}
function Findings({ file, onSave }: { file: AuditFile; onSave: Save }) {
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("0");
  const [severity, setSeverity] = useState<Finding["severity"]>("Moderate");
  return (
    <>
      <Panel title="Findings and unadjusted differences">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const n = Number(amount);
            if (!title.trim() || !Number.isFinite(n)) return;
            onSave(
              {
                ...file,
                findings: [
                  ...file.findings,
                  {
                    id: newId(),
                    title,
                    amount: n,
                    severity,
                    disposition: "open",
                    rationale: "",
                  },
                ],
              },
              "Raised audit finding",
            );
            setTitle("");
            setAmount("0");
          }}
        >
          <div className="afi-grid">
            <Field label="Finding and affected assertion / disclosure">
              <textarea
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </Field>
            <Field label="Quantified amount (₹; zero if unquantified)">
              <input
                type="number"
                step=".01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
            </Field>
            <Field label="Severity">
              <select
                value={severity}
                onChange={(e) =>
                  setSeverity(e.target.value as Finding["severity"])
                }
              >
                {["Low", "Moderate", "High"].map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </Field>
          </div>
          <button className="afi-primary">Raise finding</button>
        </form>
      </Panel>
      {file.findings.map((f) => (
        <FindingRow
          key={f.id}
          finding={f}
          onSave={(updated) =>
            onSave(
              {
                ...file,
                findings: file.findings.map((x) =>
                  x.id === f.id ? updated : x,
                ),
              },
              "Documented finding disposition",
            )
          }
        />
      ))}
    </>
  );
}
function FindingRow({
  finding: f,
  onSave,
}: {
  finding: Finding;
  onSave: (f: Finding) => void;
}) {
  const [draft, setDraft] = useState(f);
  return (
    <Panel
      title={f.title}
      aside={
        <Tag tone={f.disposition === "open" ? "amber" : "neutral"}>
          {f.severity} · {money(f.amount)}
        </Tag>
      }
    >
      <Field label="Disposition">
        <select
          value={draft.disposition}
          onChange={(e) =>
            setDraft({
              ...draft,
              disposition: e.target.value as Finding["disposition"],
            })
          }
        >
          {["open", "adjusted", "unadjusted", "reported"].map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
      </Field>
      <Field label="Evaluation, management response and opinion effect">
        <textarea
          value={draft.rationale}
          onChange={(e) => setDraft({ ...draft, rationale: e.target.value })}
        />
      </Field>
      <button
        disabled={draft.disposition !== "open" && !draft.rationale.trim()}
        onClick={() => onSave(draft)}
      >
        Save evaluation
      </button>
      <p className="afi-muted">
        Management acceptance alone does not clear an unadjusted misstatement or
        determine the opinion.
      </p>
    </Panel>
  );
}
function Completion({ file, onSave }: { file: AuditFile; onSave: Save }) {
  const [c, setC] = useState(file.completion);
  return (
    <>
      <Panel title="Completion and reporting judgement">
        <p>
          These fields document an evaluation draft. The system does not
          generate an auditor signature, certify compliance or issue a report.
        </p>
        <div className="afi-grid">
          <Field label="Draft opinion">
            <select
              value={c.opinion}
              onChange={(e) => setC({ ...c, opinion: e.target.value })}
            >
              <option value="">Needs assessment</option>
              {["Unmodified", "Qualified", "Adverse", "Disclaimer"].map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>
          </Field>
          <Field label="Proposed report date">
            <input
              type="date"
              value={c.reportDate}
              onChange={(e) => setC({ ...c, reportDate: e.target.value })}
            />
          </Field>
          <Field label="Subsequent events reviewed through">
            <input
              type="date"
              value={c.subsequentThrough}
              onChange={(e) =>
                setC({ ...c, subsequentThrough: e.target.value })
              }
            />
          </Field>
          <Field label="Written representations — signed document reference">
            <input
              value={c.representationRef}
              onChange={(e) =>
                setC({ ...c, representationRef: e.target.value })
              }
            />
          </Field>
          <Field label="Going-concern assessment and reporting effect">
            <textarea
              value={c.goingConcern}
              onChange={(e) => setC({ ...c, goingConcern: e.target.value })}
            />
          </Field>
          <Field label="Opinion rationale, including unadjusted findings">
            <textarea
              value={c.opinionRationale}
              onChange={(e) => setC({ ...c, opinionRationale: e.target.value })}
            />
          </Field>
          <Field label="Quality review completed / applicability rationale">
            <textarea
              value={c.qualityReview}
              onChange={(e) => setC({ ...c, qualityReview: e.target.value })}
            />
          </Field>
          <Field label="UDIN reference (if already issued externally)">
            <input
              value={c.udin}
              onChange={(e) => setC({ ...c, udin: e.target.value })}
            />
          </Field>
        </div>
        <button
          className="afi-primary"
          onClick={() =>
            onSave({ ...file, completion: c }, "Saved completion assessment")
          }
        >
          Save completion
        </button>
      </Panel>
      <Panel title="Review pack export">
        <p>
          Export procedure references, standards, evidence, conclusions and
          review status for inspection.
        </p>
        <button
          onClick={() =>
            download(
              "auditflow-working-papers.csv",
              exportCsv([
                [
                  "Reference",
                  "Area",
                  "Title",
                  "Standard",
                  "Status",
                  "Evidence",
                  "Conclusion",
                  "Not applicable reason",
                  "Prepared by",
                  "Reviewed by",
                ],
                ...file.procedures.map((p) => [
                  p.id,
                  p.area,
                  p.title,
                  p.standard,
                  p.status,
                  p.evidence,
                  p.conclusion,
                  p.naReason,
                  p.preparedBy ?? "",
                  p.reviewedBy ?? "",
                ]),
              ]),
              "text/csv",
            )
          }
        >
          Export working-paper register
        </button>
      </Panel>
    </>
  );
}
function Methodology() {
  return (
    <>
      <Panel title="India methodology and sources">
        <p>
          Pack {PACK_VERSION}. Source review: 18 September 2026. Original
          Ledzerly procedures draw on public workflow concepts and reference
          Indian requirements; they are not Inflo's licensed methodology.
        </p>
        <div className="afi-callout">
          <strong>Quality management</strong>
          <p>
            ICAI's 31 March 2026 announcement deferred mandatory SQM 1 and SQM 2
            implementation until further announcement; SQC 1 continues under
            that announcement. Recheck the applicable position when approving an
            engagement's methodology.
          </p>
        </div>
        <ul className="afi-sources">
          {SOURCES.map(([title, url]) => (
            <li key={url}>
              <a href={url} target="_blank" rel="noreferrer">
                {title} ↗
              </a>
            </li>
          ))}
        </ul>
        <p>
          AS and Ind AS use separate procedures for revenue, leases, tax,
          employee benefits, provisions and related parties. Company, NBFC, LLP
          and other presentation selections are prompts for applicability
          review, not automatic eligibility decisions.
        </p>
        <p>
          CARO covers {CARO_TOPICS.length} principal topics. The programme
          distinguishes standalone and consolidated reporting. Subclause
          checklists and final statutory wording still require methodology
          approval.
        </p>
      </Panel>
      <Panel title="Scope of this working build">
        <ul>
          <li>
            Available: engagement setup, applicability decisions, CSV trial
            balance, materiality, risk links, evidence references, review
            workflow, request tracking, findings, draft completion and exports.
          </li>
          <li>
            Not connected: shared server persistence, client document portal,
            file uploads, Tally / Zoho / Books extraction, transaction-level
            analytics, statistical sampling, immutable signing and final report
            generation.
          </li>
          <li>
            Tax audit forms and GST / TDS filing remain separate workstreams. No
            filing schema or portal upload is generated here.
          </li>
          <li>
            Firm approval of the content pack, security controls and production
            acceptance testing are required before using this as an official
            audit file.
          </li>
        </ul>
      </Panel>
    </>
  );
}
